import React from "react";
import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import "./PriceDetails.scss";
import { useNavigate } from "react-router-dom";
import { East } from "@mui/icons-material";

interface PriceDetailsProps {
  orderSummary: any;
  partnerId?: number;
  billingValid?: boolean;
  isFromPlans?: boolean;
}

const PriceDetails: React.FC<PriceDetailsProps> = ({
  orderSummary,
  //   partnerId,
  //   billingValid,
  isFromPlans,
}) => {
  const itemCount = orderSummary?.totalItems || 0;
  const subtotal = orderSummary?.subTotal || 0;
  const discount = orderSummary?.discountAmount ?? 0;
  const taxFee = orderSummary?.taxFee ?? 0;
  const serviceFee = orderSummary?.serviceFee ?? 0;
  const partners = orderSummary?.partnerList || [];
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const preNeedTotal = Number(orderSummary?.PreneedTotal) || Number(orderSummary?.preNeedTotal) || 0;
  const total = subtotal - discount + taxFee + serviceFee;
  const immediateNeedTotal = Number(orderSummary?.ImmediateNeedTotal) || Number(orderSummary?.immediateNeedTotal) || 0;

  const isPlanner = isFromPlans || (partners.length > 0 && partners.every((p: any) => !!p.plannerId || Array.isArray(p?.packages)));

  const renderNeedType = (needType: string) => {
    if (!needType) return null;
    const isPreNeed = needType === "pre_need";
    const label = isPreNeed ? "Preneed" : "Immediate Need";
    return (
      <>
        <Box
          component="span"
          sx={{
            color: "#084145",
            mx: 1,
            fontWeight: 900,
            fontSize: "18px",
            verticalAlign: "middle",
          }}
        >
          &bull;
        </Box>
        <Box
          component="span"
          sx={{
            color: "#000",
            fontSize: "14px",
            fontWeight: 400,
            verticalAlign: "middle",
          }}
        >
          {label}
        </Box>
      </>
    );
  };

  //   const cartId = cartSummary.cartId;

  const navigate = useNavigate();

  const handlePlaceOrder = async () => {
    navigate("payment", {
      state: {
        orderSummary: orderSummary,
      },
    });
  };

  return (
    <Box className="order-summary">
      <Typography className="order-summary__section-title">
        Price Details
      </Typography>

      {/* Subtotal Section */}
      <Box className="order-summary__box order-summary__box--top">
        <Box className="order-summary__row">
          <Typography className="order-summary__label">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </Typography>
          <Typography className="order-summary__value">
            $ {formatCurrency(subtotal ?? 0)}
          </Typography>
        </Box>
      </Box>

      {/* <Box className="order-summary__box order-summary__box--middle">
        <TextField
          placeholder="Have a coupon code?"
          className="order-summary__input-field"
          disabled
        />

        <Button className="order-summary__apply-btn" variant="outlined">
          Apply
        </Button>
      </Box> */}

      {/* Partner Breakdown Section */}
      <Box className="order-summary__box order-summary__box--details">
        {partners?.map((partnerObj: any, index: number) => {
          // If services exist, show each service name
          if (Array.isArray(partnerObj?.services) && partnerObj.services.length > 0) {
            return partnerObj.services.map((service: any, serviceIndex: number) => (
              <Box
                className="order-summary__row"
                key={`${index}-service-${serviceIndex}`}
              >
                <Typography className="order-summary__label">
                  {service?.serviceName || "-"}
                  {renderNeedType(service?.needType)}
                </Typography>
                <Typography className="order-summary__value">
                  $ {formatCurrency(service?.serviceTotalPrice ?? service?.price ?? partnerObj?.partnerTotalPrice ?? 0)}
                </Typography>
              </Box>
            ));
          }

          // If packages exist, show each package name
          if (Array.isArray(partnerObj?.packages) && partnerObj.packages.length > 0) {
            return partnerObj.packages.map((pkg: any, packageIndex: number) => (
              <Box key={`${index}-package-${packageIndex}`}>
                <Box className="order-summary__row" sx={{ marginBottom: "0px !important" }}>
                  <Typography className="order-summary__label">
                    {pkg?.packageName && (
                      <Typography sx={{ fontWeight: "600 !important", fontSize: "14px", color: "#000" }}>
                        {pkg.packageName}
                      </Typography>
                    )}
                  </Typography>
                  <Typography className="order-summary__value">
                    $ {formatCurrency(pkg?.packageTotalPrice ?? pkg?.price ?? partnerObj?.partnerTotalPrice ?? 0)}
                  </Typography>
                </Box>
              </Box>
            ));
          }

          // Fallback to old logic
          let partnerName = "";
          let partnerPrice = 0;

          if (partnerObj && typeof partnerObj === "object") {
            if (partnerObj.plannerName) {
              partnerName = partnerObj.plannerName;
              partnerPrice = partnerObj.partnerTotalPrice ?? partnerObj.price ?? 0;
            } else if (partnerObj.partnerName) {
              partnerName = partnerObj.partnerName;
              partnerPrice = partnerObj.partnerTotalPrice ?? partnerObj.price ?? 0;
            } else {
              const keys = Object.keys(partnerObj);
              if (keys.length > 0) {
                partnerName = keys[0];
                partnerPrice = partnerObj[partnerName];
              }
            }
          }

          return (
            <Box className="order-summary__row" key={index}>
              <Typography className="order-summary__label">
                {partnerName || "-"}
                {!isPlanner && renderNeedType(
                  partnerObj?.partnerPreNeedTypesTotalPrice > 0
                    ? "pre_need"
                    : "at_need"
                )}
              </Typography>
              <Typography className="order-summary__value">
                $ {formatCurrency(partnerPrice)}
              </Typography>
            </Box>
          );
        })}

        {/* Fees and Discounts */}
        {/* <Box className="order-summary__row">
          <Typography className="order-summary__discount-label">
            Discount
          </Typography>
          <Typography className="order-summary__discount-value">
            $ {discount}.00
          </Typography>
        </Box> */}

        <Box className="order-summary__row">
          <Typography className="order-summary__label">Taxes</Typography>
          <Typography className="order-summary__value">
            $ {formatCurrency(taxFee)}
          </Typography>
        </Box>

        <Divider className="order-summary__divider" />

        {/* Total */}
        <Box className="order-summary__row order-summary__row--total">
          <Typography className="order-summary__label">Total*</Typography>
          <Typography className="order-summary__value">
            $ {formatCurrency(total)}
          </Typography>
        </Box>

        {!isPlanner && (
          <>
            <Divider className="order-summary__divider" />

            <Box className="order-summary__row">
              <Typography className="order-summary__label">Preneed Total</Typography>
              <Typography className="order-summary__value">
                $ {formatCurrency(preNeedTotal)}
              </Typography>
            </Box>

            <Box className="order-summary__row">
              <Typography className="order-summary__label">
                Immediate Need Total
              </Typography>
              <Typography className="order-summary__value">
                $ {formatCurrency(immediateNeedTotal)}
              </Typography>
            </Box>
          </>
        )}

        <Typography className="order-summary__billing-info">
          *Billing applies only to Immediate Need Services and Preneed Planner on MemorialGo. Preneed Service payments will be finalized with the Provider.
        </Typography>

      </Box>
    </Box>
  );
};

export default PriceDetails;